import { CSSProperties, FC, HTMLAttributes, PropsWithChildren } from 'react';
import { cn } from '@/shared/lib/utils';
import Corner from './corner.svg?react';
import { useChatMessageContext } from '../../model/context';
import styles from './ChatMessageCloud.module.css';

/** Класс для подсветки облака: добавляется снаружи на корень облака на время подсветки */
export const CHAT_MESSAGE_CLOUD_HIGHLIGHT_CLASS = styles.highlight;

/**
 * Облако, собранное не на ChatMessageCloud (лента текстовой коммуникации): светится той же подсветкой,
 * пока предок помечен {@link CHAT_MESSAGE_CLOUD_HIGHLIGHT_CLASS}.
 */
export const CHAT_MESSAGE_CLOUD_HIGHLIGHT_TARGET_CLASS = styles.inner;

type Props = HTMLAttributes<HTMLDivElement> & {
  className?: string;
};

/**
 * Компонент "облачка" сообщения чата
 * Содержит визуальное оформление сообщения (фон, скругления, уголок)
 * Внутри размещаются ChatMessageTitle, ChatMessageContent, ChatMessageTime
 */
export const ChatMessageCloud: FC<PropsWithChildren<Props>> = ({ children, className, ...props }) => {
  const { side, isLast } = useChatMessageContext();
  const isRightSide = side === 'right';

  const corner = (
    <div className={cn(styles.corner, 'w-2 shrink-0 z-[1]')}>
      {isLast && (
        <Corner
          style={{ '--corner-size': '18px' } as CSSProperties}
          className={cn(
            'transition-colors text-(--chat-background)',
            isRightSide ? 'rotate-y-180 -translate-x-px' : '',
          )}
        />
      )}
    </div>
  );

  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_auto] items-end font-light flex-1 max-w-full ',
        !isRightSide && 'grid-cols-[auto_1fr]',
        className,
      )}
      style={
        {
          '--chat-background': isRightSide ? 'var(--color-chat-cloud-outgoing)' : 'var(--color-chat-cloud-incoming)',
          '--chat-foreground': isRightSide
            ? 'var(--color-chat-cloud-outgoing-foreground)'
            : 'var(--color-chat-cloud-incoming-foreground)',
          '--chat-time': isRightSide ? 'var(--color-chat-time-outgoing)' : 'var(--color-chat-time-incoming)',
          '--chat-event': isRightSide ? 'var(--color-chat-event-outgoing)' : 'var(--color-chat-event-incoming)',
        } as CSSProperties
      }
      {...props}
    >
      {!isRightSide && corner}
      <div
        className={cn(
          styles.inner,
          'flex flex-col max-w-full gap-2.5 p-3 rounded-3xl text-sm text-light bg-(--chat-background) overflow-hidden transition-colors',
          isRightSide ? 'ml-auto text-(--chat-foreground)' : ' mr-auto',
          isLast && (isRightSide ? 'rounded-br-none' : 'rounded-bl-none'),
        )}
      >
        {children}
      </div>
      {isRightSide && corner}
    </div>
  );
};
